// FILE: src/services/feedbackService.js
//
// Client service for the Feedback Hub. Talks to the `feedback_items` table
// (migration 040) and the admin-gated metrics RPCs.
//
// Two access paths, mirroring the rest of the app:
//   • Learner submissions are fire-and-forget — errors are logged, never
//     thrown, so a failed write never blocks the UI (matches reportService /
//     telemetryService). The success path returns the inserted row.
//   • Admin reads/updates return { data, error } so the console can surface
//     failures. RLS gates SELECT/UPDATE to admins via _is_caller_admin().
//
// No PII is written beyond what the learner volunteers (description, optional
// Discord handle). user_id is attached automatically when signed in.

import { supabase } from './supabaseClient';

// ── Constants (kept in sync with the CHECK constraints in migration 040) ─────

export const FEEDBACK_CATEGORIES = [
  'Bug Report',
  'Feature Request',
  'Question Content Issue',
  'Usability / UX Feedback',
  'Performance Issue',
  'General Suggestion',
];

export const FEEDBACK_SEVERITIES = ['Low', 'Medium', 'High'];

export const FEEDBACK_STATUSES = [
  'New',
  'In Progress',
  'Released',
  'Closed',
];

export const FEEDBACK_SOURCES = [
  'In App',
  'Discord',
  'Reddit',
  'LinkedIn',
  'Direct Message',
];

// Statuses that count as "resolved" — setting one stamps resolved_at/by.
const RESOLVED_STATUSES = new Set(['Released', 'Closed']);

// ── Helpers ──────────────────────────────────────────────────────────────────

async function currentUserId() {
  try {
    // Local session read (no network) — avoids the getUser() null-on-failure
    // fault. feedback_items.user_id stays nullable for anonymous reporters.
    const { data } = await supabase.auth.getSession();
    return data?.session?.user?.id ?? null;
  } catch (_) {
    return null;
  }
}

/**
 * Maps the public-facing input object to a snake_case feedback_items row.
 * Blank optional fields are coerced to null so filters/aggregations stay clean.
 */
function toRow(input, userId) {
  const clean = (v) => {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    return s.length ? s : null;
  };
  return {
    user_id:          userId,
    category:         input.category,
    severity:         input.severity || 'Medium',
    status:           input.status || 'New',
    title:            clean(input.title),
    description:      String(input.description || '').trim(),
    route:            clean(input.route),
    page_name:        clean(input.pageName),
    certification:    clean(input.certification),
    question_id:      clean(input.questionId),
    session_id:       clean(input.sessionId),
    exam_domain:      clean(input.examDomain),
    discord_username: clean(input.discordUsername),
    screenshot_url:   clean(input.screenshotUrl),
    source:           input.source || 'In App',
    metadata:         input.metadata || {},
  };
}

// ── Learner / submission writes (fire-and-forget) ────────────────────────────

/**
 * Submit a feedback item. Resolves to the inserted row on success, or null on
 * failure (failures are logged, never thrown).
 *
 * @param {object} input
 * @param {string} input.category      required — one of FEEDBACK_CATEGORIES
 * @param {string} input.description   required — the learner's message
 * @param {string} [input.severity]    'Low' | 'Medium' | 'High' (default Medium)
 * @param {string} [input.title]
 * @param {string} [input.route]       underlying route/view key
 * @param {string} [input.pageName]    human-readable page label
 * @param {string} [input.certification]
 * @param {string} [input.questionId]
 * @param {string} [input.sessionId]
 * @param {string} [input.examDomain]
 * @param {string} [input.discordUsername]
 * @param {string} [input.screenshotUrl]
 * @param {string} [input.source]      default 'In App'
 * @param {object} [input.metadata]
 * @returns {Promise<object|null>}
 */
export async function createFeedback(input) {
  if (!input || !input.category) {
    console.warn('[feedbackService] createFeedback: category is required');
    return null;
  }
  if (!input.description || !String(input.description).trim()) {
    console.warn('[feedbackService] createFeedback: description is required');
    return null;
  }
  try {
    const userId = await currentUserId();
    const { data, error } = await supabase
      .from('feedback_items')
      .insert(toRow(input, userId))
      .select()
      .single();
    if (error) {
      console.warn('[feedbackService] insert failed:', error.message);
      return null;
    }
    return data;
  } catch (err) {
    console.warn('[feedbackService] unexpected insert error:', err);
    return null;
  }
}

/**
 * Mirror a question report into feedback_items so the admin console is a single
 * unified repository. Additive — the legacy `reports` table write still runs in
 * reportService.persistReport(). Category is fixed to 'Question Content Issue'.
 *
 * @param {object} report
 * @param {string} report.questionId
 * @param {string} [report.referenceId]
 * @param {string} report.certification   exam id ('pmp' | 'cpmai' | ...)
 * @param {string} [report.examDomain]    question category/domain
 * @param {string} [report.sessionId]
 * @param {string} report.comments        the learner's description
 * @param {string} [report.reportCategory] the report's own sub-category
 * @param {string} [report.questionText]  snapshot of the stem
 * @returns {Promise<object|null>}
 */
export async function createFeedbackFromReport(report) {
  if (!report || !report.comments) return null;
  const refLabel = report.referenceId ? ` (${report.referenceId})` : '';
  return createFeedback({
    category:      'Question Content Issue',
    severity:      'Medium',
    description:   report.comments,
    title:         `Question report${refLabel}: ${report.reportCategory || 'issue'}`,
    route:         'question-review',
    pageName:      'Question Review',
    certification: report.certification,
    questionId:    report.questionId,
    sessionId:     report.sessionId,
    examDomain:    report.examDomain,
    source:        'In App',
    metadata:      { question_text: report.questionText || '', report_category: report.reportCategory || '' },
  });
}

// ── Admin reads ──────────────────────────────────────────────────────────────

/**
 * List feedback items with optional filters. Admin-only (RLS).
 *
 * @param {object} [filters]
 * @param {string} [filters.status]
 * @param {string} [filters.category]
 * @param {string} [filters.certification]
 * @param {string} [filters.search]   matches description/title (ilike)
 * @param {number} [filters.limit]    default 500
 * @returns {Promise<{data: object[], error: any}>}
 */
export async function listFeedback(filters = {}) {
  try {
    let q = supabase
      .from('feedback_items')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(filters.limit || 500);

    if (filters.status)        q = q.eq('status', filters.status);
    if (filters.category)      q = q.eq('category', filters.category);
    if (filters.certification) q = q.eq('certification', filters.certification);
    if (filters.search && filters.search.trim()) {
      const s = filters.search.trim().replace(/[%,]/g, ' ');
      q = q.or(`description.ilike.%${s}%,title.ilike.%${s}%`);
    }

    const { data, error } = await q;
    return { data: data || [], error };
  } catch (error) {
    return { data: [], error };
  }
}

/** Convenience: dashboard summary counts. Admin-only RPC. */
export async function getFeedbackSummary() {
  try {
    const { data, error } = await supabase.rpc('feedback_summary');
    return { data: data || null, error };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Window breakdown metrics for the telemetry panel. Admin-only RPC.
 * @param {string} fromISO 'YYYY-MM-DD'
 * @param {string} toISO   'YYYY-MM-DD'
 */
export async function getFeedbackMetrics(fromISO, toISO) {
  try {
    const { data, error } = await supabase.rpc('feedback_metrics', {
      p_from: fromISO,
      p_to:   toISO,
    });
    return { data: data || null, error };
  } catch (error) {
    return { data: null, error };
  }
}

// ── Admin writes ───────────────────────────────────────────────────────────

/**
 * Update an item's status, admin notes, or mark it duplicate/released/closed.
 * When the new status is resolving (Released/Closed/Duplicate) and resolved_at
 * is not already set, stamps resolved_at + resolved_by. Admin-only (RLS).
 *
 * @param {string} id
 * @param {object} changes  { status?, adminNotes?, resolvedBy? }
 * @returns {Promise<{data: object|null, error: any}>}
 */
export async function updateFeedback(id, changes = {}) {
  try {
    const patch = {};
    if (changes.status !== undefined)     patch.status      = changes.status;
    if (changes.adminNotes !== undefined) patch.admin_notes = changes.adminNotes;

    if (changes.status && RESOLVED_STATUSES.has(changes.status)) {
      patch.resolved_at = new Date().toISOString();
      if (changes.resolvedBy) patch.resolved_by = changes.resolvedBy;
    } else if (changes.status) {
      // Re-opening clears the resolution stamp.
      patch.resolved_at = null;
      patch.resolved_by = null;
    }

    const { data, error } = await supabase
      .from('feedback_items')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    return { data: data || null, error };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Admin manual creation of an item received off-platform (Discord, Reddit, …).
 * Thin wrapper over createFeedback that requires an explicit source.
 */
export async function createManualFeedback(input) {
  return createFeedback({ ...input, source: input.source || 'Direct Message' });
}
